// app/mappers/approvalDataSource.ts
import {
  gxPaymentPendingViewSummaryApi,
  gxRTPPaymentApprovalListApi,
  gxBeneficiaryPendingListApi,
} from "@/app/apiConfig/services/paymentsAbstractApiLayer";
import {
  gxPaymentsBulkApprovalViewSummaryApi,
  gxPayrollBulkApprovalViewSummaryApi,
} from "@/app/apiConfig/services/bulkPaymentsPayrollApiLayer";
import { gxDashboardPendingApprovalViewSummaryApi } from "@/app/apiConfig/services/customerAbstractApiLayer";
import { Constants } from "@/app/utils/AppConstants";
import type {
  ApprovalCardItem,
  ApprovalRawRecord,
  ApprovalTabId,
} from "../types/approval";
import { mapApprovalRecordToCardItem } from "./approvalMapper";

export interface ApprovalPageParams {
  pageNo: number;
  pageSize: number;
}

const DEFAULT_PAGE_PARAMS: ApprovalPageParams = { pageNo: 1, pageSize: 10 };

const toArray = <T>(data: unknown): T[] =>
  Array.isArray(data) ? data : data ? [data as T] : [];

const PENDING_APPROVAL_STATUS_CODE = "4415";

// gxDashboardPendingApprovalViewSummaryApi moduleType -> our internal tabId
export const moduleTypeToTabId = (
  moduleType: string,
): Exclude<ApprovalTabId, "all"> | null => {
  switch (moduleType?.toUpperCase()) {
    case "BENEFICIARY":
      return "bene";
    case "RTP":
      return "rtp";
    case "BULK_PAYMENT": // was "BULK"
      return "bulk";
    case "PAYROLL":
      return "payrolls";
    case "TRANSACTIONS": // was "PAYMENT"
      return "txn";
    default:
      return null;
  }
};

// --- each source is native shape -> the shared ApprovalRawRecord ---
const adaptTxn = (r: any): ApprovalRawRecord => ({
  transId: r.transId,
  category: "txn",
  debitBank: r.debitBankName,
  beneNickname: r.beneNickname ?? r.beneAccountName,
  crtdDt: r.paymentInitiatedDate ?? r.paymentDate,
  gxChnNo: r.gxChnNo,
  debitAmount: r.debitAmount,
  currencySymbol: r.debitCurrency,
  numberOfApproved: r.numberOfApproved,
  numberOfLevels: r.numberOfLevels,
  paymentStatusCode: r.paymentStatusCode,
  original: r,
  accountName: r.accountName ?? "--",
  bankName: r.bankName ?? "--",
  beneAccountName: r.beneAccountName ?? "--",
});

const adaptRtp = (r: any): ApprovalRawRecord => ({
  transId: r.transId,
  category: "rtp",
  debitBank: r.debitBank,
  beneNickname: r.beneNickname,
  crtdDt: r.crtdDt,
  gxChnNo: r.gxChnNo,
  debitAmount: r.debitAmount,
  currencySymbol: r.currencySymbol,
  numberOfApproved: r.numberOfApproved,
  numberOfLevels: r.numberOfLevels,
  paymentStatusCode: r.paymentStatusCode,
  original: r,
  accountName: r.accountName ?? "--",
  bankName: r.bankName ?? "--",
  beneAccountName: r.beneAccountName ?? "--",
});

const adaptBene = (r: any): ApprovalRawRecord => ({
  transId: r.beneId,
  category: "bene",
  debitBank: r.beneBankName ?? "--",
  beneNickname: r.beneNickname ?? r.beneName ?? "--",
  crtdDt: r.crtdDt,
  // gxChnNo: r.cbsBeneId ?? String(r.beneId),
  gxChnNo: "",
  debitAmount: 0,
  currencySymbol: r.beneCurrency ?? "--",
  numberOfApproved: 0,
  numberOfLevels: r.noOfApprLevel ?? 1,
  paymentStatusCode: undefined,
  original: r,
  accountName: r.accountName ?? "--",
  bankName: r.bankName ?? "--",
  beneBankName: r.beneBankName ?? "--",
  beneAccountHolderName: r.beneAccountHolderName ?? r.beneName ?? "--",
});

const adaptBulkPayment = (r: any): ApprovalRawRecord => ({
  transId: r.gxJobProcessid,
  category: "bulk",
  debitBank: r.bankName ?? r.debitBankName ?? "--",
  beneNickname: r.fileName ?? "--",
  crtdDt: r.crtdDt,
  gxChnNo: r.gxJobChnNo,
  debitAmount: r.totalAmount ?? 0,
  currencySymbol: r.baseCurrency,
  numberOfApproved: 0,
  numberOfLevels: r.numberOfLevels ?? 1,
  paymentStatusCode: r.jobStatusCode,
  original: r,
  accountName: r.accountName ?? "--",
  bankName: r.bankName ?? "--",
});

const adaptBulkPayroll = (r: any): ApprovalRawRecord => ({
  transId: r.gxJobProcessid,
  category: "payrolls",
  debitBank: r.bankName ?? r.debitBankName ?? "--",
  beneNickname: r.fileName ?? "--",
  crtdDt: r.crtdDt,
  gxChnNo: r.gxJobChnNo,
  debitAmount: r.totalAmount ?? 0,
  currencySymbol: undefined,
  numberOfApproved: 0,
  numberOfLevels: r.numberOfLevels ?? 1,
  paymentStatusCode: r.jobStatusCode,
  original: r,
  accountName: r.accountName ?? "--",
  bankName: r.bankName ?? "--",
});

// gxDashboardPendingApprovalViewSummaryApi flat shape (used for the "all" tab)
const adaptDashboardPendingApproval = (r: any): ApprovalRawRecord | null => {
  const category = moduleTypeToTabId(r.moduleType);
  if (!category) return null;

  return {
    transId: r.recordId,
    category,
    debitBank: r.debitBankName ?? "--",
    beneNickname: r.beneAccountName ?? "--",
    crtdDt: r.paymentDate,
    gxChnNo: r.gxChnNo,
    debitAmount: r.debitAmount ?? 0,
    currencySymbol: r.debitCurrency ?? "--",
    numberOfApproved: r.noOfApproved ?? 0,
    numberOfLevels: r.noOfApprLevel ?? 1,
    paymentStatusCode: undefined,
    original: r,
    accountName: r.accountName ?? "--",
    bankName: r.bankName ?? "--",
  };
};

// const fetchTab = async (
//   tabId: Exclude<ApprovalTabId, "all">,
// ): Promise<ApprovalCardItem[]> => {
//   switch (tabId) {
//     case "txn": {
//       const res = await gxPaymentPendingViewSummaryApi({
//         pageNo: 1,
//         pageSize: 10,
//         sortBy: Constants.ascending,
//         status: `${Constants.awaitingApproval}`,
//       });
//       return (
//         toArray<any>(res?.data)
//           .map(adaptTxn)
//           // .map(mapApprovalRecordToCardItem);
//           .map((r) => mapApprovalRecordToCardItem(r))
//       );
//     }
//     case "rtp": {
//       const res = await gxRTPPaymentApprovalListApi({
//         pageNo: 1,
//         pageSize: 10,
//         sortBy: "Asc",
//         status: "4415",
//         search: "",
//       });
//       return (
//         toArray<any>(res?.data)
//           .map(adaptRtp)
//           // .map(mapApprovalRecordToCardItem);
//           .map((r) => mapApprovalRecordToCardItem(r))
//       );
//     }
//     case "bene": {
//       const res = await gxBeneficiaryPendingListApi({
//         status: "3015",
//         pageNo: 1,
//         sortBy: "ASC",
//         pageSize: 10,
//       });
//       return (
//         toArray<any>(res?.data)
//           .map(adaptBene)
//           // .map(mapApprovalRecordToCardItem);
//           .map((r) => mapApprovalRecordToCardItem(r))
//       );
//     }
//     case "bulk": {
//       const res = await gxPaymentsBulkApprovalViewSummaryApi({
//         pageNo: 1,
//         pageSize: "10",
//       });
//       return (
//         toArray<any>(res?.data)
//           .filter((r) => r.jobStatusCode === 4515)
//           .map(adaptBulkPayment)
//           // .map(mapApprovalRecordToCardItem);
//           .map((r) => mapApprovalRecordToCardItem(r))
//       );
//     }
//     case "payrolls": {
//       const res = await gxPayrollBulkApprovalViewSummaryApi({
//         pageNo: 1,
//         pageSize: "10",
//       });
//       return (
//         toArray<any>(res?.data)
//           .filter((r) => r.jobStatusCode === 7515)
//           .map(adaptBulkPayroll)
//           // .map(mapApprovalRecordToCardItem);
//           .map((r) => mapApprovalRecordToCardItem(r))
//       );
//     }
//   }
// };

const fetchTab = async (
  tabId: Exclude<ApprovalTabId, "all">,
  { pageNo, pageSize }: ApprovalPageParams = DEFAULT_PAGE_PARAMS,
): Promise<ApprovalCardItem[]> => {
  switch (tabId) {
    case "txn": {
      const res = await gxPaymentPendingViewSummaryApi({
        pageNo,
        pageSize,
        sortBy: Constants.ascending,
        status: `${Constants.awaitingApproval}`,
      });
      return toArray<any>(res?.data)
        .map(adaptTxn)
        .map((r) => mapApprovalRecordToCardItem(r));
    }
    case "rtp": {
      const res = await gxRTPPaymentApprovalListApi({
        pageNo,
        pageSize,
        sortBy: "Asc",
        status: "4415",
        search: "",
      });
      return toArray<any>(res?.data)
        .map(adaptRtp)
        .map((r) => mapApprovalRecordToCardItem(r));
    }
    case "bene": {
      const res = await gxBeneficiaryPendingListApi({
        status: "3015",
        pageNo,
        sortBy: "ASC",
        pageSize,
      });
      return toArray<any>(res?.data)
        .map(adaptBene)
        .map((r) => mapApprovalRecordToCardItem(r));
    }
    case "bulk": {
      const res = await gxPaymentsBulkApprovalViewSummaryApi({
        pageNo,
        pageSize: String(pageSize),
      });
      return toArray<any>(res?.data)
        .filter((r) => r.jobStatusCode === 4515)
        .map(adaptBulkPayment)
        .map((r) => mapApprovalRecordToCardItem(r));
    }
    case "payrolls": {
      const res = await gxPayrollBulkApprovalViewSummaryApi({
        pageNo,
        pageSize: String(pageSize),
      });
      return toArray<any>(res?.data)
        .filter((r) => r.jobStatusCode === 7515)
        .map(adaptBulkPayroll)
        .map((r) => mapApprovalRecordToCardItem(r));
    }
  }
};

// export const getApprovalData = async (
//   tabId: ApprovalTabId,
// ): Promise<ApprovalCardItem[]> => {
//   if (tabId === "all") {
//     const res = await gxDashboardPendingApprovalViewSummaryApi({
//       pageNo: 1,
//       pageSize: 100,
//       status: PENDING_APPROVAL_STATUS_CODE,
//     });
//     return (
//       toArray<any>(res?.data)
//         .map(adaptDashboardPendingApproval)
//         .filter((r): r is ApprovalRawRecord => r !== null)
//         // .map(mapApprovalRecordToCardItem);
//         .map((r) => mapApprovalRecordToCardItem(r, "all"))
//     ); //  mark as all
//   }
//   return fetchTab(tabId);
// };

export const getApprovalData = async (
  tabId: ApprovalTabId,
  pageParams: ApprovalPageParams = DEFAULT_PAGE_PARAMS,
): Promise<ApprovalCardItem[]> => {
  if (tabId === "all") {
    const res = await gxDashboardPendingApprovalViewSummaryApi({
      pageNo: 1,
      pageSize: 100,
      status: PENDING_APPROVAL_STATUS_CODE,
    });
    return toArray<any>(res?.data)
      .map(adaptDashboardPendingApproval)
      .filter((r): r is ApprovalRawRecord => r !== null)
      .map((r) => mapApprovalRecordToCardItem(r, "all"));
  }
  return fetchTab(tabId, pageParams);
};

// // app/mappers/approvalDataSource.ts
// import {
//   gxPaymentPendingViewSummaryApi,
//   gxRTPPaymentApprovalListApi,
//   gxBeneficiaryPendingListApi,
// } from "@/app/apiConfig/services/paymentsAbstractApiLayer";
// import {
//   gxPaymentsBulkApprovalViewSummaryApi,
//   gxPayrollBulkApprovalViewSummaryApi,
// } from "@/app/apiConfig/services/bulkPaymentsPayrollApiLayer";
// import { gxDashboardPendingApprovalViewSummaryApi } from "@/app/apiConfig/services/customerAbstractApiLayer";
// import { Constants } from "@/app/utils/AppConstants";
// import type {
//   ApprovalCardItem,
//   ApprovalRawRecord,
//   ApprovalTabId,
// } from "../types/approval";
// import { mapApprovalRecordToCardItem } from "./approvalMapper";

// export interface ApprovalPageParams {
//   pageNo: number;
//   pageSize: number;
// }

// const DEFAULT_PAGE_PARAMS: ApprovalPageParams = { pageNo: 1, pageSize: 10 };

// const toArray = <T>(data: unknown): T[] =>
//   Array.isArray(data) ? data : data ? [data as T] : [];

// const PENDING_APPROVAL_STATUS_CODE = "4415";

// // gxDashboardPendingApprovalViewSummaryApi moduleType -> our internal tabId
// const moduleTypeToTabId = (
//   moduleType: string,
// ): Exclude<ApprovalTabId, "all"> | null => {
//   switch (moduleType?.toUpperCase()) {
//     case "BENEFICIARY":
//       return "bene";
//     case "RTP":
//       return "rtp";
//     case "BULK_PAYMENT": // was "BULK"
//       return "bulk";
//     case "PAYROLL":
//       return "payrolls";
//     case "TRANSACTIONS": // was "PAYMENT"
//       return "txn";
//     default:
//       return null;
//   }
// };

// // --- each source is native shape -> the shared ApprovalRawRecord ---
// const adaptTxn = (r: any): ApprovalRawRecord => ({
//   transId: r.transId,
//   category: "txn",
//   debitBank: r.debitBankName,
//   beneNickname: r.beneNickname ?? r.beneAccountName,
//   crtdDt: r.paymentInitiatedDate ?? r.paymentDate,
//   gxChnNo: r.gxChnNo,
//   debitAmount: r.debitAmount,
//   currencySymbol: r.debitCurrency,
//   numberOfApproved: r.numberOfApproved,
//   numberOfLevels: r.numberOfLevels,
//   paymentStatusCode: r.paymentStatusCode,
//   original: r,
//   accountName: r.accountName ?? "--",
//   bankName: r.bankName ?? "--",
//   beneAccountName: r.beneAccountName ?? "--",
// });

// const adaptRtp = (r: any): ApprovalRawRecord => ({
//   transId: r.transId,
//   category: "rtp",
//   debitBank: r.debitBank,
//   beneNickname: r.beneNickname,
//   crtdDt: r.crtdDt,
//   gxChnNo: r.gxChnNo,
//   debitAmount: r.debitAmount,
//   currencySymbol: r.currencySymbol,
//   numberOfApproved: r.numberOfApproved,
//   numberOfLevels: r.numberOfLevels,
//   paymentStatusCode: r.paymentStatusCode,
//   original: r,
//   accountName: r.accountName ?? "--",
//   bankName: r.bankName ?? "--",
//   beneAccountName: r.beneAccountName ?? "--",
// });

// const adaptBene = (r: any): ApprovalRawRecord => ({
//   transId: r.beneId,
//   category: "bene",
//   debitBank: r.beneBankName ?? "--",
//   beneNickname: r.beneNickname ?? r.beneName ?? "--",
//   crtdDt: r.crtdDt,
//   // gxChnNo: r.cbsBeneId ?? String(r.beneId),
//   gxChnNo: "",
//   debitAmount: 0,
//   currencySymbol: r.beneCurrency ?? "--",
//   numberOfApproved: 0,
//   numberOfLevels: r.noOfApprLevel ?? 1,
//   paymentStatusCode: undefined,
//   original: r,
//   accountName: r.accountName ?? "--",
//   bankName: r.bankName ?? "--",
//   beneBankName: r.beneBankName ?? "--",
//   beneAccountHolderName: r.beneAccountHolderName ?? r.beneName ?? "--",
// });

// const adaptBulkPayment = (r: any): ApprovalRawRecord => ({
//   transId: r.gxJobProcessid,
//   category: "bulk",
//   debitBank: r.bankName ?? r.debitBankName ?? "--",
//   beneNickname: r.fileName ?? "--",
//   crtdDt: r.crtdDt,
//   gxChnNo: r.gxJobChnNo,
//   debitAmount: r.totalAmount ?? 0,
//   currencySymbol: r.baseCurrency,
//   numberOfApproved: 0,
//   numberOfLevels: r.numberOfLevels ?? 1,
//   paymentStatusCode: r.jobStatusCode,
//   original: r,
//   accountName: r.accountName ?? "--",
//   bankName: r.bankName ?? "--",
// });

// const adaptBulkPayroll = (r: any): ApprovalRawRecord => ({
//   transId: r.gxJobProcessid,
//   category: "payrolls",
//   debitBank: r.bankName ?? r.debitBankName ?? "--",
//   beneNickname: r.fileName ?? "--",
//   crtdDt: r.crtdDt,
//   gxChnNo: r.gxJobChnNo,
//   debitAmount: r.totalAmount ?? 0,
//   currencySymbol: undefined,
//   numberOfApproved: 0,
//   numberOfLevels: r.numberOfLevels ?? 1,
//   paymentStatusCode: r.jobStatusCode,
//   original: r,
//   accountName: r.accountName ?? "--",
//   bankName: r.bankName ?? "--",
// });

// // gxDashboardPendingApprovalViewSummaryApi flat shape (used for the "all" tab)
// const adaptDashboardPendingApproval = (r: any): ApprovalRawRecord | null => {
//   const category = moduleTypeToTabId(r.moduleType);
//   if (!category) return null;

//   return {
//     transId: r.recordId,
//     category,
//     debitBank: r.debitBankName ?? "--",
//     beneNickname: r.beneAccountName ?? "--",
//     crtdDt: r.paymentDate,
//     gxChnNo: r.gxChnNo,
//     debitAmount: r.debitAmount ?? 0,
//     currencySymbol: r.debitCurrency ?? "--",
//     numberOfApproved: r.noOfApproved ?? 0,
//     numberOfLevels: r.noOfApprLevel ?? 1,
//     paymentStatusCode: undefined,
//     original: r,
//     accountName: r.accountName ?? "--",
//     bankName: r.bankName ?? "--",
//   };
// };

// // const fetchTab = async (
// //   tabId: Exclude<ApprovalTabId, "all">,
// // ): Promise<ApprovalCardItem[]> => {
// //   switch (tabId) {
// //     case "txn": {
// //       const res = await gxPaymentPendingViewSummaryApi({
// //         pageNo: 1,
// //         pageSize: 10,
// //         sortBy: Constants.ascending,
// //         status: `${Constants.awaitingApproval}`,
// //       });
// //       return (
// //         toArray<any>(res?.data)
// //           .map(adaptTxn)
// //           // .map(mapApprovalRecordToCardItem);
// //           .map((r) => mapApprovalRecordToCardItem(r))
// //       );
// //     }
// //     case "rtp": {
// //       const res = await gxRTPPaymentApprovalListApi({
// //         pageNo: 1,
// //         pageSize: 10,
// //         sortBy: "Asc",
// //         status: "4415",
// //         search: "",
// //       });
// //       return (
// //         toArray<any>(res?.data)
// //           .map(adaptRtp)
// //           // .map(mapApprovalRecordToCardItem);
// //           .map((r) => mapApprovalRecordToCardItem(r))
// //       );
// //     }
// //     case "bene": {
// //       const res = await gxBeneficiaryPendingListApi({
// //         status: "3015",
// //         pageNo: 1,
// //         sortBy: "ASC",
// //         pageSize: 10,
// //       });
// //       return (
// //         toArray<any>(res?.data)
// //           .map(adaptBene)
// //           // .map(mapApprovalRecordToCardItem);
// //           .map((r) => mapApprovalRecordToCardItem(r))
// //       );
// //     }
// //     case "bulk": {
// //       const res = await gxPaymentsBulkApprovalViewSummaryApi({
// //         pageNo: 1,
// //         pageSize: "10",
// //       });
// //       return (
// //         toArray<any>(res?.data)
// //           .filter((r) => r.jobStatusCode === 4515)
// //           .map(adaptBulkPayment)
// //           // .map(mapApprovalRecordToCardItem);
// //           .map((r) => mapApprovalRecordToCardItem(r))
// //       );
// //     }
// //     case "payrolls": {
// //       const res = await gxPayrollBulkApprovalViewSummaryApi({
// //         pageNo: 1,
// //         pageSize: "10",
// //       });
// //       return (
// //         toArray<any>(res?.data)
// //           .filter((r) => r.jobStatusCode === 7515)
// //           .map(adaptBulkPayroll)
// //           // .map(mapApprovalRecordToCardItem);
// //           .map((r) => mapApprovalRecordToCardItem(r))
// //       );
// //     }
// //   }
// // };

// const fetchTab = async (
//   tabId: Exclude<ApprovalTabId, "all">,
//   { pageNo, pageSize }: ApprovalPageParams = DEFAULT_PAGE_PARAMS,
// ): Promise<ApprovalCardItem[]> => {
//   switch (tabId) {
//     case "txn": {
//       const res = await gxPaymentPendingViewSummaryApi({
//         pageNo,
//         pageSize,
//         sortBy: Constants.ascending,
//         status: `${Constants.awaitingApproval}`,
//       });
//       return toArray<any>(res?.data)
//         .map(adaptTxn)
//         .map((r) => mapApprovalRecordToCardItem(r));
//     }
//     case "rtp": {
//       const res = await gxRTPPaymentApprovalListApi({
//         pageNo,
//         pageSize,
//         sortBy: "Asc",
//         status: "4415",
//         search: "",
//       });
//       return toArray<any>(res?.data)
//         .map(adaptRtp)
//         .map((r) => mapApprovalRecordToCardItem(r));
//     }
//     case "bene": {
//       const res = await gxBeneficiaryPendingListApi({
//         status: "3015",
//         pageNo,
//         sortBy: "ASC",
//         pageSize,
//       });
//       return toArray<any>(res?.data)
//         .map(adaptBene)
//         .map((r) => mapApprovalRecordToCardItem(r));
//     }
//     case "bulk": {
//       const res = await gxPaymentsBulkApprovalViewSummaryApi({
//         pageNo,
//         pageSize: String(pageSize),
//       });
//       return toArray<any>(res?.data)
//         .filter((r) => r.jobStatusCode === 4515)
//         .map(adaptBulkPayment)
//         .map((r) => mapApprovalRecordToCardItem(r));
//     }
//     case "payrolls": {
//       const res = await gxPayrollBulkApprovalViewSummaryApi({
//         pageNo,
//         pageSize: String(pageSize),
//       });
//       return toArray<any>(res?.data)
//         .filter((r) => r.jobStatusCode === 7515)
//         .map(adaptBulkPayroll)
//         .map((r) => mapApprovalRecordToCardItem(r));
//     }
//   }
// };

// // export const getApprovalData = async (
// //   tabId: ApprovalTabId,
// // ): Promise<ApprovalCardItem[]> => {
// //   if (tabId === "all") {
// //     const res = await gxDashboardPendingApprovalViewSummaryApi({
// //       pageNo: 1,
// //       pageSize: 100,
// //       status: PENDING_APPROVAL_STATUS_CODE,
// //     });
// //     return (
// //       toArray<any>(res?.data)
// //         .map(adaptDashboardPendingApproval)
// //         .filter((r): r is ApprovalRawRecord => r !== null)
// //         // .map(mapApprovalRecordToCardItem);
// //         .map((r) => mapApprovalRecordToCardItem(r, "all"))
// //     ); //  mark as all
// //   }
// //   return fetchTab(tabId);
// // };

// export const getApprovalData = async (
//   tabId: ApprovalTabId,
//   pageParams: ApprovalPageParams = DEFAULT_PAGE_PARAMS,
// ): Promise<ApprovalCardItem[]> => {
//   if (tabId === "all") {
//     const res = await gxDashboardPendingApprovalViewSummaryApi({
//       pageNo: 1,
//       pageSize: 100,
//       status: PENDING_APPROVAL_STATUS_CODE,
//     });
//     return toArray<any>(res?.data)
//       .map(adaptDashboardPendingApproval)
//       .filter((r): r is ApprovalRawRecord => r !== null)
//       .map((r) => mapApprovalRecordToCardItem(r, "all"));
//   }
//   return fetchTab(tabId, pageParams);
// };
